import Phaser from "phaser";
import i18next from "i18next";
import { handleSceneResize } from "../utils/camera-utils";
import { isTouchDevice } from "../utils/mobile-utils";
import {
  registerDisconnectHandler,
  unregisterDisconnectHandler,
} from "../network";
import { COLORS, hexToPhaser } from "../ui/colors";
import { trackEvent } from "../utils/ga-events";

export class LobbyScene extends Phaser.Scene {
  private displayedArcProgress: number = 0;
  private arcTween?: Phaser.Tweens.Tween;
  private logoPortal!: Phaser.GameObjects.Image;
  private textBackground!: Phaser.GameObjects.Image;
  private progressText!: Phaser.GameObjects.Text;
  private playerCountText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private playerIndicators: Phaser.GameObjects.Rectangle[] = [];
  private background!: Phaser.GameObjects.Image;
  private lobbyMusic: Phaser.Sound.BaseSound | null = null;

  private remainingTime: number = 10; // 10 seconds
  private maxTime: number = 10;
  private slotCount: number = 1;
  private maxSlots: number = 4; // Will be updated from server
  private slotTypes: ("player" | "bot" | "squad")[] = ["player"]; // Track player types

  private timerEvent?: Phaser.Time.TimerEvent;
  private spinnerRotation: number = 0;
  private spinnerTimer?: Phaser.Time.TimerEvent;

  private selectedMap: string = "forest"; // Track selected map
  private mapThumbnails: Array<{
    name: string;
    image: Phaser.GameObjects.Image;
    border?: Phaser.GameObjects.Rectangle;
  }> = []; // Store map thumbnails
  private availableMaps: string[] = []; // Store list of available maps from server
  private animatedMapIndex: number = 0; // Track which map is currently highlighted in animation
  private mapAnimationTween?: Phaser.Tweens.Tween; // Tween for smooth animation
  private mapCycleProgress: number = 0; // Continuous progress through map cycles
  private mapAnimationActive: boolean = false; // Whether animation is currently active
  private shouldStopOnSelected: boolean = false; // Whether to stop when we reach the selected map
  private mapSelectionComplete: boolean = false; // Whether we've completed the selection and should not animate anymore
  private mapHighlightBar?: Phaser.GameObjects.Rectangle; // Yellow highlight bar
  private mapThumbnailPositions: number[] = []; // X positions of each thumbnail
  private mapHighlightBlinkTween?: Phaser.Tweens.Tween; // Tween for blinking highlight

  private tips: string[] = []; // Tips loaded from tips.json
  private currentTipIndex: number = 0; // Currently displayed tip
  private tipsText?: Phaser.GameObjects.Text; // Text object for cycling tips
  private tipsTimer?: Phaser.Time.TimerEvent; // Timer for cycling through tips
  private statusText2?: Phaser.GameObjects.Text; // "Waiting for players..." text

  constructor() {
    super("LobbyScene");
  }

  preload() {
    // Load the background image
    this.load.image("lobby-bg", "sprites/lobby-bg.png");
    // Load the logo portal
    this.load.image("logo-portal", "icons/logo-portal-only.png");
    // Load the logo circle
    this.load.image("logo-circle", "icons/logo-circle.png");
    // Load the lobby music
    this.load.audio("lobby", "sfx/lobby.ogg");
    // Load tips from localized JSON based on current language
    const currentLang = i18next.language || "en";
    this.load.json("tips", `locales/${currentLang}/tips.json`);
    // Dynamically load all map thumbnail images
    const mapNames = ["forest", "hotel", "wasteland"];
    mapNames.forEach((mapName) => {
      this.load.image(`map-${mapName}`, `maps/images/${mapName}.png`);
    });
  }

  create() {
    const { width, height } = this.scale;

    // Track GA event: player enters lobby
    trackEvent("enter-lobby");

    // Emit join-lobby message to server
    if (window.socket) {
      window.socket.emit("join-lobby");
    }

    // Add the background image and center it
    this.background = this.add.image(width / 2, height / 2, "lobby-bg");
    this.background.setOrigin(0.5);
    this.background.setDisplaySize(width, height);

    // Set background color
    this.cameras.main.setBackgroundColor("#000000");

    // Define circle parameters
    const logoSize = 200;
    const centerX = this.cameras.main.width * 0.75 + 20; // Moved 20px right
    const centerY = this.cameras.main.centerY;

    // Calculate title position based on device and screen size
    const titleY = this.calculateTitlePosition();

    // Create title text (positioned above spinner, aligned to right)
    const titleText = this.add
      .text(centerX, titleY, i18next.t("scenes:lobby.title"), {
        fontSize: "32px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        stroke: "#000000",
        strokeThickness: 4,
        shadow: {
          offsetX: 0,
          offsetY: 4,
          color: "#000000",
          blur: 0,
          stroke: true,
          fill: true,
        },
      })
      .setOrigin(0.5)
      .setDepth(10); // Render on top of highlight bar

    // Add spinning portal
    this.logoPortal = this.add.image(centerX, centerY, "logo-portal");
    this.logoPortal.setDisplaySize(logoSize, logoSize);
    this.logoPortal.setOrigin(0.5);

    // Add continuous rotation tween for the portal
    this.tweens.add({
      targets: this.logoPortal,
      angle: 360,
      duration: 3000,
      repeat: -1,
      ease: "Linear",
    });

    // Add logo circle background behind text
    this.textBackground = this.add.image(centerX, centerY, "logo-circle");
    this.textBackground.setDisplaySize(100, 100);
    this.textBackground.setOrigin(0.5);

    this.playerCountText = this.add
      .text(centerX, centerY, i18next.t("scenes:lobby.slotCount", { current: this.slotCount, max: this.maxSlots }), {
        fontSize: "14px",
        color: COLORS.GREEN,
        fontFamily: "Anton",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    // Progress text in center of circle (bottom part)
    this.progressText = this.add
      .text(centerX, centerY - 20, this.formatTime(this.remainingTime), {
        fontSize: "18px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    // Create player indicator squares under the countdown
    this.createSlotIndicators(centerX, centerY);

    // Create status text below the title (moved up)
    this.statusText2 = this.add
      .text(centerX, titleY + 30, i18next.t("scenes:lobby.waitingForPlayers"), {
        fontSize: "14px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        stroke: "#000000",
        strokeThickness: 2,
      })
      .setOrigin(0.5)
      .setDepth(10); // Render on top of highlight bar

    // Create map thumbnails
    this.createMapThumbnails();

    // Load and initialize tips
    this.loadAndShuffleTips();

    // Tips cycling text (where instructions used to be)
    this.tipsText = this.add
      .text(
        centerX,
        centerY + logoSize / 2 + 50,
        this.tips[0] ||
          i18next.t("scenes:lobby.matchStartInfo"),
        {
          fontSize: "16px",
          color: COLORS.YELLOW,
          fontFamily: "Anton",
          stroke: "#000000",
          strokeThickness: 3,
          wordWrap: { width: 220, useAdvancedWrap: true },
          align: "center",
        },
      )
      .setOrigin(0.5)
      .setDepth(10); // Render on top of highlight bar

    // Start cycling tips every 8 seconds
    this.startTipsCycle();

    // Listen for resize events to maintain camera zoom
    this.scale.on("resize", this.handleResize, this);

    // Set up socket listeners
    this.setupSocketListeners();

    // Register disconnect handler to return to loading scene on connection loss
    registerDisconnectHandler(this.game);

    // Play lobby music
    if (this.cache.audio.exists("lobby") && localStorage.getItem("musicEnabled") !== "false") {
      this.lobbyMusic = this.sound.add("lobby", { volume: 0.5, loop: true });
      this.lobbyMusic.play();
    }

    // Set up shutdown event to clean up listeners when scene stops
    this.events.once("shutdown", this.shutdown, this);

    // Initial display update
    this.updateDisplay();
  }

  private handleResize = (gameSize: Phaser.Structs.Size) => {
    handleSceneResize(this, gameSize);

    const { width, height } = gameSize;

    // Resize and reposition background
    if (this.background) {
      this.background.setPosition(width / 2, height / 2);
      this.background.setDisplaySize(width, height);
    }
  };

  private calculateTitlePosition(): number {
    const cameraHeight = this.cameras.main.height;
    const centerY = this.cameras.main.centerY;

    // On mobile devices, adjust title position to ensure proper spacing
    if (isTouchDevice()) {
      // Calculate minimum spacing needed above the spinner
      const circleRadius = 60;
      const minSpacingAboveSpinner = 100; // Minimum space between title and spinner
      const spinnerTop = centerY - circleRadius;
      const maxTitleY = spinnerTop - minSpacingAboveSpinner;

      // Use smaller of calculated position or safe position
      // This ensures title is never too close to spinner on small screens
      const defaultTitleY = Math.min(cameraHeight * 0.15, 50);
      return Math.min(defaultTitleY, maxTitleY);
    } else {
      // Desktop: use fixed position
      return 50;
    }
  }

  private createSlotIndicators(centerX: number, centerY: number) {
    const squareSize = 12;
    const spacing = 4;
    const indicatorsPerRow = 4;
    const rowSpacing = 4; // Vertical spacing between rows

    // Calculate width for 4 indicators
    const totalWidth =
      squareSize * indicatorsPerRow + spacing * (indicatorsPerRow - 1);
    const startX = centerX - totalWidth / 2 + squareSize / 2;
    const startY = centerY + 20; // Position under the countdown text

    // Create squares in 2 rows (4 per row)
    for (let i = 0; i < this.maxSlots; i++) {
      const row = Math.floor(i / indicatorsPerRow);
      const col = i % indicatorsPerRow;
      const x = startX + col * (squareSize + spacing);
      const y = startY + row * (squareSize + rowSpacing);
      const square = this.add.rectangle(
        x,
        y,
        squareSize,
        squareSize,
        hexToPhaser(COLORS.BLACK),
      );
      this.playerIndicators.push(square);
    }
  }

  private createMapThumbnails() {
    // Use available maps from server, or fallback to default list
    const mapNames =
      this.availableMaps.length > 0
        ? this.availableMaps
        : ["forest", "hotel", "wasteland"];

    // Make thumbnails as tall as screen, maintain aspect ratio
    const thumbnailHeight = this.cameras.main.height;
    const thumbnailWidth = thumbnailHeight / 3.85; // 30% thicker than previous
    const spacing = 20; // Spacing between thumbnails
    const totalWidth =
      mapNames.length * thumbnailWidth + (mapNames.length - 1) * spacing;
    const startX = thumbnailWidth / 2 + (isTouchDevice() ? 75 : 45); // Far left with minimal padding, moved 20px right
    const startY = this.cameras.main.centerY; // Center vertically

    // Create the yellow highlight bar that spans full screen height
    this.mapHighlightBar = this.add.rectangle(
      startX,
      this.cameras.main.centerY,
      thumbnailWidth * 1.2, // 10px wider on each side of the thumbnail
      thumbnailHeight, // Same height as thumbnail
    );
    this.mapHighlightBar.setOrigin(0.5);
    this.mapHighlightBar.setFillStyle(hexToPhaser(COLORS.YELLOW), 1); // Opaque yellow
    this.mapHighlightBar.setDepth(2); // Behind thumbnails

    mapNames.forEach((mapName, index) => {
      const x = startX + index * (thumbnailWidth + spacing);
      const y = startY;

      // Store thumbnail x position
      this.mapThumbnailPositions.push(x);

      // Create thumbnail image - display with wider aspect ratio, cropped on top/bottom
      const imageKey = `map-${mapName}`;
      const thumbnail = this.add.image(x, y, imageKey);
      // Set width to fill thumbnail, height scales based on image aspect ratio (landscape images will crop top/bottom)
      // 10px smaller all around (20px total reduction) and 60px less in height
      thumbnail.setDisplaySize(
        thumbnailWidth * 1.2 - 20,
        thumbnailHeight - 120,
      );
      thumbnail.setOrigin(0.5);
      thumbnail.setDepth(3); // In front of highlight bar

      // White border outlining the thumbnail
      const border = this.add.rectangle(
        x,
        y,
        thumbnailWidth * 1.2 - 20,
        thumbnailHeight - 120,
      );
      border.setOrigin(0.5);
      border.setStrokeStyle(2, 0xffffff, 1);
      border.setDepth(4); // In front of thumbnail

      // Store for later updates
      this.mapThumbnails.push({
        name: mapName,
        image: thumbnail,
        border,
      });
    });
  }

  private updateMapThumbnailBorder() {
    const thumbnailHeight = 160;
    const thumbnailWidth = 80;

    // Move highlight bar to selected map when animation stops
    if (this.mapHighlightBar && this.mapThumbnailPositions.length > 0) {
      const selectedIndex = this.mapThumbnails.findIndex(
        (thumb) => thumb.name === this.selectedMap,
      );
      if (selectedIndex >= 0) {
        const targetX = this.mapThumbnailPositions[selectedIndex];
        this.mapHighlightBar.setX(targetX);
        this.tweens.add({
          targets: this.mapHighlightBar,
          alpha: 1,
          duration: 300,
          ease: "Power2.easeInOut",
        });

        // Start blinking if time is less than 10 seconds
        if (this.remainingTime < 10) {
          this.startBlinkingHighlight();
        }
      }
    }
  }

  private startBlinkingHighlight() {
    // Stop existing blink tween if running
    if (this.mapHighlightBlinkTween) {
      this.mapHighlightBlinkTween.stop();
    }

    // Slow fade in/out blink
    if (this.mapHighlightBar) {
      this.mapHighlightBlinkTween = this.tweens.add({
        targets: this.mapHighlightBar,
        fillAlpha: 0.4,
        duration: 600,
        ease: "Sine.easeInOut",
        yoyo: true,
        repeat: -1, // Loop infinitely
      });
    }
  }

  private stopBlinkingHighlight() {
    if (this.mapHighlightBlinkTween) {
      this.mapHighlightBlinkTween.stop();
      this.mapHighlightBlinkTween = undefined;
    }
    if (this.mapHighlightBar) {
      this.mapHighlightBar.setFillStyle(hexToPhaser(COLORS.YELLOW), 1);
    }
  }

  private animateMapHighlight() {
    // Stop existing animation if running
    if (this.mapAnimationTween) {
      this.mapAnimationTween.stop();
      this.mapAnimationTween = undefined;
    }

    // 1 second per map: stay on each image for 1 second before moving
    const durationPerMap = 2000;
    const totalCycleDuration = durationPerMap * this.mapThumbnails.length;

    // Animate continuously through map cycles
    this.mapAnimationTween = this.tweens.add({
      targets: this,
      mapCycleProgress: this.mapCycleProgress + this.mapThumbnails.length,
      duration: totalCycleDuration,
      repeat: -1, // Loop infinitely until stopped
      onUpdate: () => {
        const mapIndex =
          Math.floor(this.mapCycleProgress) % this.mapThumbnails.length;

        // Only update if index changed
        if (mapIndex !== this.animatedMapIndex) {
          this.animatedMapIndex = mapIndex;
          this.updateAnimatedBorderWithFade();

          // Check if we should stop (only if shouldStopOnSelected is true and we're on the selected map)
          if (
            this.shouldStopOnSelected &&
            this.mapThumbnails[mapIndex].name === this.selectedMap
          ) {
            if (this.mapAnimationTween) {
              this.mapAnimationTween.stop();
              this.mapAnimationTween = undefined;
            }
            this.mapAnimationActive = false;
            this.mapSelectionComplete = true; // Mark selection as complete
            // Bar is already at correct position from smooth animation, just start blinking
            this.time.delayedCall(100, () => {
              this.startBlinkingHighlight();
            });
          }
        }
      },
    });

    this.mapAnimationActive = true;
  }

  private updateMapAnimation() {
    // If selection is already complete, don't do anything more
    if (this.mapSelectionComplete) {
      return;
    }

    // Update the flag to stop when we hit the selected map
    if (this.remainingTime < 14) {
      this.shouldStopOnSelected = true;
    } else {
      this.shouldStopOnSelected = false;
    }

    // If animation is not active, start it
    if (!this.mapAnimationActive) {
      this.animateMapHighlight();
    }
  }

  private updateAnimatedBorderWithFade() {
    // Smoothly move highlight bar to new position
    if (this.mapHighlightBar && this.mapThumbnailPositions.length > 0) {
      const targetX = this.mapThumbnailPositions[this.animatedMapIndex];

      // Smooth movement to next map
      this.tweens.add({
        targets: this.mapHighlightBar,
        x: targetX,
        duration: 300,
        ease: "Power2.easeInOut",
      });
    }
  }

  private flashHighlightBar() {
    // Stop the slow blinking first
    this.stopBlinkingHighlight();

    // Flash the highlight bar with brighter fill when selection is made
    if (this.mapHighlightBar) {
      // Blink opacity
      this.tweens.add({
        targets: this.mapHighlightBar,
        fillAlpha: 0.5,
        duration: 150,
        ease: "Sine.easeInOut",
        yoyo: true,
        repeat: 5, // Total of 6 transitions (3 full cycles)
        onComplete: () => {
          // After blinking, return to opaque
          if (this.mapHighlightBar) {
            this.mapHighlightBar.setFillStyle(hexToPhaser(COLORS.YELLOW), 1);
          }
        },
      });
    }
  }

  private updateAnimatedBorder() {
    // Border styling removed since borders were removed from thumbnails
  }

  private updateSlotIndicators() {
    // Update each square based on playerTypes from lobby-update
    for (let i = 0; i < this.playerIndicators.length; i++) {
      const square = this.playerIndicators[i];
      if (i < this.slotTypes.length) {
        const playerType = this.slotTypes[i];
        if (playerType === "player") {
          square.setFillStyle(hexToPhaser(COLORS.GREEN));
        } else if (playerType === "squad") {
          square.setFillStyle(hexToPhaser(COLORS.BLUE));
        } else {
          square.setFillStyle(hexToPhaser(COLORS.WHITE));
        }
      } else {
        square.setFillStyle(hexToPhaser(COLORS.BLACK));
      }
    }
  }

  private setupSocketListeners() {
    if (!window.socket) return;

    // Listen for squad-match-blocking (squad member is in active match, can't join lobby)
    window.socket.on("squad-match-blocking", (data: { message: string }) => {
      console.warn("[LobbyScene] Received squad-match-blocking:", data.message);
      // Similar to match-end, transition to base with message
      this.cleanup();
      this.scene.stop("LobbyScene");
      this.scene.start("BaseScene", {
        squadBlockingMessage: data.message,
      });
    });

    // Listen for lobby updates from server
    window.socket.on(
      "lobby-update",
      (data: {
        playerCount: number;
        remainingTime: number;
        maxTime: number;
        maxPlayers: number;
        playerTypes?: ("player" | "bot" | "squad")[];
        selectedMap?: string;
        availableMaps?: string[];
      }) => {
        const prevArcProgress = this.slotCount / this.maxSlots;
        this.slotCount = data.playerCount;
        this.remainingTime = data.remainingTime;
        this.maxTime = data.maxTime;
        this.maxSlots = data.maxPlayers;
        // Update available maps if provided
        if (data.availableMaps) {
          this.availableMaps = data.availableMaps;
        }
        // Update selected map if provided
        if (data.selectedMap) {
          this.selectedMap = data.selectedMap;
        }
        // Update or start map animation based on remaining time
        this.updateMapAnimation();
        // Update player types if provided, otherwise assume all are players
        if (data.playerTypes) {
          this.slotTypes = data.playerTypes;
        } else {
          // Default: first player is always a player, others are bots if they join
          this.slotTypes = Array(this.slotCount)
            .fill(0)
            .map((_, i) => (i === 0 ? "player" : "bot"));
        }
        // Animate arc progress
        const newArcProgress = this.slotCount / this.maxSlots;
        if (this.arcTween) {
          this.arcTween.stop();
        }
        this.arcTween = this.tweens.add({
          targets: this,
          displayedArcProgress: newArcProgress,
          duration: 400,
          ease: "Sine.easeInOut",
          onUpdate: () => this.updateDisplay(),
          onComplete: () => {
            this.displayedArcProgress = newArcProgress;
          },
        });
        // ...other display updates (except arc)
        this.updateDisplay();
      },
    );

    // Listen for match start
    window.socket.on(
      "match-start",
      (data: { duration: number; selectedMap?: string }) => {
        if (this.slotTypes.some((t) => t === "squad")) {
          trackEvent("squad-match");
        }
        this.cleanup();
        // Store match duration for MainScene
        window.matchDuration = data.duration;
        // Store selected map for MainScene - use data.selectedMap or fallback to lobby's selectedMap
        const mapToLoad = data.selectedMap || this.selectedMap || "forest";
        (window as any).selectedMap = mapToLoad;
        console.log(
          `[LobbyScene] Match starting with map: ${mapToLoad}`,
          `(from server: ${data.selectedMap}, from lobby: ${this.selectedMap})`,
        );
        // Properly stop this scene before starting MainScene
        this.scene.stop("LobbyScene");
        // Use helper to safely start MainScene
        if (this.spinnerTimer) {
          this.spinnerTimer.destroy();
          this.spinnerTimer = undefined;
        }
        import("../index").then((mod) => {
          mod.startMainScene();
        });
      },
    );
  }

  private updateDisplay() {
    // Update player count text (top part of circle)
    this.playerCountText.setText(i18next.t("scenes:lobby.slotCount", { current: this.slotCount, max: this.maxSlots }));

    // Update time text (bottom part of circle)
    this.progressText.setText(this.formatTime(this.remainingTime));

    // Update player indicator squares
    this.updateSlotIndicators();

    // Update status text based on state
    if (this.statusText2) {
      if (this.slotCount >= this.maxSlots) {
        this.statusText2.setText(i18next.t("scenes:lobby.findingComplete"));
      } else {
        this.statusText2.setText(i18next.t("scenes:lobby.waitingForPlayers"));
      }
    }
  }

  private formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  private shutdown() {
    // Called when scene is stopped/transitioned away from
    console.info("[LobbyScene] Shutting down, cleaning up listeners");

    this.scale.off("resize", this.handleResize, this);

    // Unregister disconnect handler
    unregisterDisconnectHandler();

    // Clean up map animation tween
    if (this.mapAnimationTween) {
      this.mapAnimationTween.stop();
      this.mapAnimationTween = undefined;
    }
    this.shouldStopOnSelected = false;
    this.mapSelectionComplete = false;

    // Clean up tips timer
    if (this.tipsTimer) {
      this.tipsTimer.destroy();
      this.tipsTimer = undefined;
    }

    // Clean up socket listeners
    if (window.socket) {
      window.socket.off("lobby-update");
      window.socket.off("match-start");
      window.socket.off("squad-match-blocking");
    }

    // Stop lobby music
    if (this.lobbyMusic) {
      this.lobbyMusic.stop();
      this.lobbyMusic = null;
    }
  }

  private cleanup() {
    if (this.timerEvent) {
      this.timerEvent.destroy();
      this.timerEvent = undefined;
    }

    // Clean up tips timer
    if (this.tipsTimer) {
      this.tipsTimer.destroy();
      this.tipsTimer = undefined;
    }

    // Clean up UI elements
    if (this.logoPortal) {
      this.logoPortal.destroy();
    }
    if (this.textBackground) {
      this.textBackground.destroy();
    }
    if (this.progressText) {
      this.progressText.destroy();
    }
    if (this.playerCountText) {
      this.playerCountText.destroy();
    }
    if (this.statusText) {
      this.statusText.destroy();
    }
    if (this.statusText2) {
      this.statusText2.destroy();
    }
    if (this.tipsText) {
      this.tipsText.destroy();
    }
    if (this.background) {
      this.background.destroy();
    }

    // Clean up player indicators
    this.playerIndicators.forEach((indicator) => {
      if (indicator) {
        indicator.destroy();
      }
    });
    this.playerIndicators = [];

    // Clean up map thumbnails
    this.mapThumbnails.forEach((thumb) => {
      if (thumb.image) {
        thumb.image.destroy();
      }
      if (thumb.border) {
        thumb.border.destroy();
      }
    });
    this.mapThumbnails = [];
  }

  destroy() {
    // Ensure shutdown is called
    this.shutdown();
    this.cleanup();
  }

  private loadAndShuffleTips() {
    // Load tips from the preloaded JSON file
    const tipsData = this.cache.json.get("tips");
    if (Array.isArray(tipsData)) {
      this.tips = [...tipsData]; // Make a copy
      // Shuffle using Fisher-Yates algorithm
      for (let i = this.tips.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [this.tips[i], this.tips[j]] = [this.tips[j], this.tips[i]];
      }
    }
    this.currentTipIndex = 0;
  }

  private startTipsCycle() {
    // Set up timer to cycle through tips every 8 seconds
    if (this.tipsTimer) {
      this.tipsTimer.destroy();
    }

    this.tipsTimer = this.time.addEvent({
      delay: 8000, // 8 seconds
      callback: () => {
        this.showNextTip();
      },
      loop: true,
    });
  }

  private showNextTip() {
    if (this.tips.length === 0 || !this.tipsText) {
      return;
    }

    // Move to next tip, wrapping around if at the end
    this.currentTipIndex = (this.currentTipIndex + 1) % this.tips.length;
    const newTip = this.tips[this.currentTipIndex];

    // Update the text with fade effect
    this.tweens.add({
      targets: this.tipsText,
      alpha: 0.5,
      duration: 200,
      ease: "Sine.easeInOut",
      onComplete: () => {
        if (this.tipsText) {
          this.tipsText.setText(newTip);
          this.tweens.add({
            targets: this.tipsText,
            alpha: 1,
            duration: 200,
            ease: "Sine.easeInOut",
          });
        }
      },
    });
  }
}
